<script lang="ts">
import type { AsTag } from 'reka-ui';

/**
 * Accessible toggle switch for binary settings that take effect immediately.
 *
 * Use for "notifications on/off", "dark mode", "auto-save" — anything where the
 * change commits the instant the user toggles it. For form state held until submit,
 * use a checkbox instead; the visual difference signals when the change applies.
 *
 * ## Custom value types
 *
 * Generic over `T` (default `boolean`). To bind v-model to a non-boolean state,
 * declare `trueValue` and `falseValue` together:
 *
 *     const status = ref<'active' | 'paused'>('paused')
 *     // template: TSwitch v-model="status" :true-value="'active'" :false-value="'paused'"
 *
 * The "on" position emits `trueValue`; the "off" position emits `falseValue`. Useful
 * for state machines, form payloads with string enums, or anywhere boolean coercion
 * would lose semantic meaning. Reach for it when v-model would otherwise need a
 * derived boolean adapter — not for simple on/off, which is the 95% case.
 *
 * The legacy `value` prop is `boolean`-only; don't mix it with non-boolean generic mode.
 *
 * ## Theming
 *
 * The "on" track resolves to `var(--color-bg-primary-default)` — the tenant brand.
 * Wrap a subtree in `TThemeProvider` with a brand override to retheme at runtime
 * (no re-renders; CSS variable cascade only).
 */
export interface TSwitchProps<T = boolean> {
  /** Visible label rendered next to the switch. Also serves as the accessible name. */
  label?: string;

  /** Disables interaction. @defaultValue false */
  disabled?: boolean;

  /**
   * Initial / fallback checked state. **Deprecated — prefer `v-model`** (`modelValue`).
   * Kept for the pre-migration `:value` + `@input` API; removal in a future major.
   * Boolean-only — don't mix with non-boolean generic mode.
   */
  value?: boolean;

  /** Two-way bound state. Idiomatic usage: `v-model="enabled"`. */
  modelValue?: T;

  /** Value emitted when the switch is "on". Default: `true` (when T = boolean). */
  trueValue?: T;

  /** Value emitted when the switch is "off". Default: `false` (when T = boolean). */
  falseValue?: T;

  /** Position of the label relative to the switch. @defaultValue 'right' */
  labelPosition?: 'left' | 'right';

  /** Explicit id for the underlying control. Auto-generated via `useId()` if omitted. */
  id?: string;

  /**
   * Element the outer wrapper renders as. Use `'label'` to make the entire row
   * clickable (native `label for=...` toggles the switch), `'li'` when placing
   * switches inside a `ul`, or a component for polymorphic rendering.
   * @defaultValue 'div'
   */
  as?: AsTag;
}

export interface TSwitchEmits<T = boolean> {
  /** Fires on every toggle (mouse, Space, Enter). Payload is the new value. */
  change: [value: T];
  /** Mirrors `change`. **Deprecated** — use `@change` or `v-model`. */
  input: [value: T];
}

export interface TSwitchSlots {
  /** Override the default label rendering. Receives the resolved `label` prop. */
  label?: (props: { label: string | undefined }) => unknown;
}
</script>

<script setup lang="ts" generic="T = boolean">
import { useForwardedAttrs } from '@composables/forwardedAttrs';
import { Label, Primitive, SwitchRoot, SwitchThumb } from 'reka-ui';
import { computed, useId } from 'vue';

defineOptions({
  name: 'TSwitch',
  inheritAttrs: false,
});

const {
  modelValue,
  value,
  trueValue,
  falseValue,
  label,
  labelPosition = 'right',
  disabled,
  id,
  as = 'div',
} = defineProps<TSwitchProps<T>>();

const emit = defineEmits<TSwitchEmits<T>>();
defineSlots<TSwitchSlots>();

const model = defineModel<T>();
// LEGACY COMPAT: folds the deprecated `value` prop into the model so pre-v-model
// consumers using `:value` + `@input` keep working (still live in webapp-frontend
// HorizontalToggle / PortalHorizontalField and frontend AtoaSteps). Drop this — with
// the `value` prop and `input` emit — once those migrate to v-model.
const forwardedModelValue = computed<T | undefined>(() => modelValue ?? (value as T | undefined));

const generatedId = useId();
const switchId = computed(() => id ?? generatedId);

const { wrapperAttrs, controlAttrs } = useForwardedAttrs();

const onUpdate = (next: T) => {
  model.value = next;
  emit('change', next);
  emit('input', next); // LEGACY COMPAT: alias of `change` for pre-v-model `@input` consumers.
};
</script>

<template>
  <Primitive
    v-bind="wrapperAttrs"
    :as
    class="t-switch"
    :data-label-position="labelPosition"
    :data-disabled="disabled || undefined"
  >
    <SwitchRoot
      :id="switchId"
      v-bind="controlAttrs"
      :model-value="forwardedModelValue"
      :true-value
      :false-value
      :disabled
      class="t-switch-track"
      @update:model-value="onUpdate"
    >
      <SwitchThumb class="t-switch-thumb" />
    </SwitchRoot>

    <Label
      v-if="label || $slots.label"
      :as="as === 'label' ? 'span' : 'label'"
      :for="as === 'label' ? undefined : switchId"
      class="t-switch-label"
    >
      <slot name="label" :label>
        {{ label }}
      </slot>
    </Label>
  </Primitive>
</template>

<style scoped lang="scss">
$track-width: 2.5rem;
$track-height: 1.25rem;
$thumb-size: 1rem;
$thumb-inset: 2px;
$thumb-translate: calc(#{$track-width} - #{$thumb-size} - #{$thumb-inset} - #{$thumb-inset});

.t-switch {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);

  &[data-label-position='left'] {
    flex-direction: row-reverse;
  }

  &[data-disabled] {
    cursor: not-allowed;
    opacity: var(--opacity-disabled);
  }
}

.t-switch-track {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: $track-width;
  height: $track-height;
  padding: 0;
  border: 0;
  border-radius: var(--radius-pill-default);
  background: var(--color-gray-3);
  cursor: pointer;
  outline: var(--focus-ring-width) solid transparent;
  outline-offset: var(--focus-ring-offset);
  transition:
    background-color var(--motion-control-duration) var(--motion-control-easing),
    outline-color var(--motion-control-duration) var(--motion-control-easing),
    box-shadow var(--motion-control-duration) var(--motion-control-easing);

  &[data-state='checked'] {
    background: var(--color-bg-primary-default);
  }

  &:not([data-disabled]):hover {
    background: var(--color-gray-4);

    &[data-state='checked'] {
      background: var(--color-bg-primary-hover);
    }
  }

  &[data-disabled]:not([data-state='checked']) {
    background: var(--color-gray-2);
  }

  &[data-disabled] {
    cursor: not-allowed;
  }

  &:focus-visible {
    outline-color: var(--color-ring-focus-default);
    box-shadow: 0 0 0 var(--focus-ring-halo-width) var(--color-ring-focus-halo-default);
  }
}

.t-switch-thumb {
  display: block;
  width: $thumb-size;
  height: $thumb-size;
  margin: $thumb-inset;
  border-radius: var(--radius-pill-default);
  background: var(--color-bg-surface-raised-default);
  box-shadow: var(--shadow-sm);
  transition:
    transform var(--motion-control-duration) var(--motion-control-easing),
    box-shadow var(--motion-control-duration) var(--motion-control-easing);
  will-change: transform;

  &[data-state='checked'] {
    transform: translateX($thumb-translate);
  }

  .t-switch-track:not([data-disabled]):active & {
    box-shadow: none;
  }

  .t-switch-track[data-disabled] & {
    box-shadow: none;
  }
}

.t-switch-label {
  font-family: var(--font-family-base);
  font-size: var(--font-b2-font-size);
  line-height: var(--font-b2-line-height);
  font-weight: var(--font-weight-regular);
  color: var(--color-text-default-default);
  cursor: pointer;
  user-select: none;

  .t-switch[data-disabled] & {
    cursor: not-allowed;
    color: var(--color-text-disabled-default);
  }
}
</style>
